import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router';

import type { RouteEntry } from '@/app/route-manifest';
import { SPACE_ROOTS, useAppMode, useIsSignedIn, useMe, useSessionStore } from '@/stores/session.store';

/** La destination après connexion, portée par `?next=` : un lien partagé mène où il doit. */
export function nextFromSearch(search: string): string | null {
  const next = new URLSearchParams(search).get('next');
  // Seulement un chemin du site : jamais une adresse ailleurs (redirection ouverte).
  return next && next.startsWith('/') && !next.startsWith('//') ? next : null;
}

/** Une route réservée aux comptes connectés : sinon, l'accueil public, qui ramènera ici. */
export function RequireSession({ children }: { children: ReactNode }) {
  const signedIn = useIsSignedIn();
  const location = useLocation();
  if (!signedIn) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={next === '/' ? '/welcome' : `/welcome?next=${encodeURIComponent(next)}`} replace />;
  }
  return children;
}

/** Les écrans d'entrée : un compte déjà connecté n'a rien à y faire. */
export function GuestOnly({ children }: { children: ReactNode }) {
  const signedIn = useIsSignedIn();
  const me = useMe();
  const mode = useAppMode();
  const location = useLocation();
  if (signedIn) {
    if (me && me.status !== 'ACTIVE') {
      return <Navigate to="/account-suspended" replace />;
    }
    return <Navigate to={nextFromSearch(location.search) ?? SPACE_ROOTS[mode]} replace />;
  }
  return children;
}

/**
 * La garde propre à chaque route : compte désactivé, espace de travail.
 *
 * - Un compte désactivé n'atteint que ce que le serveur lui laisse (commandes,
 *   litiges, discussions, réglages) : le reste renvoie au mur qui dit pourquoi.
 * - Une route d'espace ouverte depuis un autre espace : un responsable qui ouvre
 *   `/seller` bascule dans son outil ; un compte sans boutique, un
 *   administrateur ou un agent retrouvent leur propre racine.
 */
export function RouteGuard({ entry, children }: { entry: RouteEntry; children: ReactNode }) {
  const me = useMe();
  const mode = useAppMode();
  const setMode = useSessionStore((state) => state.setMode);

  const suspended = me !== null && me.status !== 'ACTIVE';
  const canSell = me ? me.ownedShopIds.length > 0 || me.memberShopIds.length > 0 : false;
  const allowed = !entry.modes || entry.modes.includes(mode);
  const switchToSeller = !allowed && entry.modes?.includes('seller') && mode === 'buyer' && canSell;

  useEffect(() => {
    if (switchToSeller) {
      setMode('seller');
    }
  }, [switchToSeller, setMode]);

  if (entry.access === 'private' && suspended && !entry.suspended) {
    return <Navigate to="/account-suspended" replace />;
  }
  if (entry.path === '/account-suspended' && me !== null && !suspended) {
    return <Navigate to={SPACE_ROOTS[mode]} replace />;
  }
  if (!allowed && !switchToSeller) {
    return <Navigate to={SPACE_ROOTS[mode]} replace />;
  }
  if (switchToSeller) {
    return null;
  }
  return children;
}

/**
 * Le compte a changé : le cache du précédent doit partir. Un ordinateur se
 * partage, et rien ne vidait le cache entre deux sessions : le suivant voyait,
 * le temps d'un rechargement, le panier et les discussions de l'autre.
 */
export function useClearCacheOnAccountChange(): void {
  const accountId = useMe()?.id ?? null;
  const queryClient = useQueryClient();
  const previous = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const before = previous.current;
    previous.current = accountId;
    if (before === undefined || before === accountId) {
      return;
    }
    queryClient.clear();
  }, [accountId, queryClient]);
}
